import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { brandStatsFixture } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('brand-stats');

/**
 * Normalisiert die echte `/v1/admin/stats`-Response (Stand 2026-06):
 *
 * Live-Shape (Stand jetzt — Core liefert flach, ohne Zeit-Partitionierung):
 *   {
 *     revenue:  { paidCount, paidTotal (string!), totalOrders },
 *     users:    { totalUsers, activeUsers (string!), suspendedUsers (string!) },
 *     services: { total, active, suspended, failed }     // active/etc. = null bei total=0
 *   }
 *
 * Wir wandeln Strings in Zahlen, normalisieren null → 0, und liefern eine
 * konsistente Shape an die Dashboard-Komponente.
 *
 * @returns {{
 *   revenue: { paidCount, paidTotal, totalOrders, currency },
 *   users:   { total, active, suspended },
 *   services:{ total, active, suspended, failed },
 *   generatedAt: string,
 *   raw: object,
 * } | null}
 */
function normalizeStats(raw) {
  if (!raw) return null;
  const num = (v, fallback = 0) => {
    if (v == null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    revenue: {
      paidCount: num(raw.revenue?.paidCount),
      paidTotal: num(raw.revenue?.paidTotal),
      totalOrders: num(raw.revenue?.totalOrders),
      currency: raw.revenue?.currency ?? 'EUR',
    },
    users: {
      total: num(raw.users?.totalUsers ?? raw.users?.total),
      active: num(raw.users?.activeUsers ?? raw.users?.active),
      suspended: num(raw.users?.suspendedUsers ?? raw.users?.suspended),
    },
    services: {
      total: num(raw.services?.total),
      active: num(raw.services?.active),
      suspended: num(raw.services?.suspended),
      failed: num(raw.services?.failed),
    },
    generatedAt: raw.generatedAt ?? new Date().toISOString(),
    raw,
  };
}

/** GET /v1/admin/stats — normalisiert die Response in eine stable Shape */
export function useBrandStats(brandOrigin) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['brandStats', brandOrigin, fixturesMode ? 'fixture' : 'live'],
    enabled: !!brandOrigin,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return normalizeStats(brandStatsFixture(brandOrigin));
      }
      const res = await fetch('/v1/admin/stats');
      dlog('/v1/admin/stats raw', res);
      return normalizeStats(res);
    },
    staleTime: 60_000,
  });
}
